import { captureEvent } from '../utils/posthogClient';
import { PH_DIRECT } from '../utils/posthogEvents';
import axios from 'axios';
import Cookies from 'js-cookie';
import React, { createContext, useCallback, useEffect, useState } from 'react';
import {
  checkIfPatientEnrolledInWeightlossProgramin6Months,
  checkNotifications,
  checkSubscriptionAuthorize,
} from '../DB/DB';

const DocContext = createContext();

const DocProvider = ({ children }) => {
  const [context, setContext] = useState({
    user: undefined,
    isWeightlossDiscount: true,
    isWeightlossDiscountEligible: true,
    hasSubscription: undefined,
  });

  useEffect(() => {
    const load = async () => {
      const sessionToken = Cookies.get('sessionToken');

      if (sessionToken) {
        try {
          const { data } = await axios.get('https://24doc.b4a.io/users/me', {
            headers: {
              'X-Parse-Application-Id': process.env.NEXT_PUBLIC_PARSE_APP_ID,
              'X-Parse-Javascript-Key': process.env.NEXT_PUBLIC_PARSE_JS_KEY,
              'X-Parse-Session-Token': sessionToken,
              'Content-Type': 'application/json',
            },
          });

          // Let anything gated on `user` (the header, most of all) update
          // the instant we know who's logged in, instead of waiting on
          // notifications/subscription/enrollment lookups too.
          setContext((context) => {
            return { ...context, user: data };
          });

          const sides = await sideEffects(data);
          setContext((context) => {
            return {
              ...context,
              ...sides,
            };
          });
        } catch (e) {
          setContext((context) => {
            return { ...context, user: null };
          });
        }
      } else {
        setContext((context) => {
          return { ...context, user: null, hasSubscription: false };
        });
      }
    };
    load();
  }, []);
  return (
    <DocContext.Provider value={{ ...context, setContext }}>
      {children}
    </DocContext.Provider>
  );
};

const useDocContext = () => {
  const context = React.useContext(DocContext);
  return context;
};

const useLoadProfile = () => {
  const { setContext } = React.useContext(DocContext);
  const loadProfile = useCallback(async () => {
    const sessionToken = Cookies.get('sessionToken');
    if (sessionToken) {
      try {
        const { data } = await axios.get('https://24doc.b4a.io/users/me', {
          headers: {
            'X-Parse-Application-Id': process.env.NEXT_PUBLIC_PARSE_APP_ID,
            'X-Parse-Javascript-Key': process.env.NEXT_PUBLIC_PARSE_JS_KEY,
            'X-Parse-Session-Token': sessionToken,
            'Content-Type': 'application/json',
          },
        });
        setContext((context) => {
          return { ...context, user: data };
        });
        const sides = await sideEffects(data);
        setContext((context) => {
          return { ...context, ...sides };
        });
      } catch (e) {
        setContext((context) => {
          return { ...context, user: null };
        });
        console.error(e, error);
      }
    } else {
      setContext((context) => {
        return { ...context, user: null };
      });
    }
    return;
  }, [setContext]);
  return loadProfile;
};

const useLogin = () => {
  const { setContext } = React.useContext(DocContext);
  const login = useCallback(
    async (email, password) => {
      try {
        const { data: user } = await axios.get('https://24doc.b4a.io/login', {
          headers: {
            'X-Parse-Application-Id': process.env.NEXT_PUBLIC_PARSE_APP_ID,
            'X-Parse-Javascript-Key': process.env.NEXT_PUBLIC_PARSE_JS_KEY,
            'X-Parse-Revocable-Session': 1,
            'Content-Type': 'application/json',
          },
          params: {
            username: email.trim().toLowerCase(),
            password: password,
          },
        });
        Cookies.set('sessionToken', user.sessionToken, { expires: 360 });
        const sides = await sideEffects(user);
        setContext((context) => {
          return {
            ...context,
            ...sides,
          };
        });
        return { success: true, user: user };
      } catch (e) {
        console.log(e);
        // Reported here rather than at each call site, so every way into
        // sign-in is counted: the modal, the diagnosis flow and /login.
        //
        // `reason` separates a rejected credential from the site being
        // unreachable, which would otherwise be indistinguishable in the
        // numbers. It carries no address and says nothing about whether the
        // account exists, see PH_DIRECT.LOGIN_FAILED.
        captureEvent(PH_DIRECT.LOGIN_FAILED, {
          reason:
            e?.response?.data?.code === 101
              ? 'invalid_credentials'
              : e?.response?.status
              ? `http_${e.response.status}`
              : 'network_error',
        });
        return { success: false, error: e.message };
      }
    },
    [setContext]
  );

  return login;
};
const useSocialLogin = () => {
  const { setContext } = React.useContext(DocContext);
  const login = useCallback(
    async (email) => {
      try {
        const { data: user } = await axios.get('https://24doc.b4a.io/login', {
          headers: {
            'X-Parse-Application-Id': process.env.NEXT_PUBLIC_PARSE_APP_ID,
            'X-Parse-Javascript-Key': process.env.NEXT_PUBLIC_PARSE_JS_KEY,
            'X-Parse-Revocable-Session': 1,
            'Content-Type': 'application/json',
          },
          params: {
            username: email.trim().toLowerCase(),
          },
        });
        Cookies.set('sessionToken', user.sessionToken, { expires: 360 });
        setContext((context) => {
          return { ...context, user: user };
        });
        return 'SUCCESS';
      } catch (e) {
        console.log(e);
        return 'FAILURE';
      }
    },
    [setContext]
  );

  return login;
};
const useSignup = () => {
  const { setContext } = React.useContext(DocContext);
  const signup = useCallback(
    async (signupData) => {
      try {
        const { data: user } = await axios.post(
          'https://24doc.b4a.io/users',
          {
            ...signupData,
            dob: {
              __type: 'Date',
              iso: new Date(
                `${signupData?.dob?.month}/${signupData?.dob?.day}/${signupData?.dob?.year}`
              ),
            },
            dateObject: signupData.dob,
          },
          {
            headers: {
              'X-Parse-Application-Id': process.env.NEXT_PUBLIC_PARSE_APP_ID,
              'X-Parse-Javascript-Key': process.env.NEXT_PUBLIC_PARSE_JS_KEY,
              'X-Parse-Revocable-Session': 1,
              'Content-Type': 'application/json',
            },
          }
        );

        Cookies.set('sessionToken', user.sessionToken, { expires: 360 });
        const sides = await sideEffects(user);
        const { password, ...safeSignupData } = signupData;
        setContext((context) => {
          return {
            ...context,
            ...sides,
            user: {
              name: signupData.name,
              email: signupData?.email.trim().toLowerCase(),
              username: signupData?.email.trim().toLowerCase(),
              ...safeSignupData,
              dateObject: signupData.dob,
              objectId: user.objectId,
            },
          };
        });
        return { success: true, data: user };
      } catch (error) {
        return { success: false, error: error.response.data.error };
      }
    },
    [setContext]
  );

  return signup;
};
const useGoogleSignup = () => {
  const { setContext } = React.useContext(DocContext);
  const randomPassword = Math.random().toString(36).slice(-8);
  const googleSignup = useCallback(
    async ({ name, family_name, given_name, email, picture }) => {
      const formattedEmail = email.trim().toLowerCase();

      const userPayload = {
        name: name,
        last_name: family_name,
        first_name: given_name,
        email: formattedEmail,
        username: formattedEmail,
        image: picture,
        password: randomPassword,
      };

      const headers = {
        'X-Parse-Application-Id': process.env.NEXT_PUBLIC_PARSE_APP_ID,
        'X-Parse-Javascript-Key': process.env.NEXT_PUBLIC_PARSE_JS_KEY,
        'X-Parse-Revocable-Session': 1,
        'Content-Type': 'application/json',
      };

      try {
        const { data: user } = await axios.post(
          'https://24doc.b4a.io/users',
          userPayload,
          { headers }
        );

        Cookies.set('sessionToken', user.sessionToken, { expires: 360 });

        setContext((prevContext) => ({
          ...prevContext,
          user: {
            ...user,
            name,
            email: formattedEmail,
            username: formattedEmail,
          },
        }));

        return { success: true, data: user };
      } catch (error) {
        // Optionally log the error for debugging
        return { success: false, data: error.response.data };
      }
    },
    [setContext]
  );

  return googleSignup;
};
export {
  DocContext,
  DocProvider,
  useDocContext,
  useGoogleSignup,
  useLoadProfile,
  useLogin,
  useSignup,
  useSocialLogin,
};

async function sideEffects(data) {
  const [notifications, subscription, ConsultationCount] = await Promise.all([
    checkNotifications(data?.objectId),
    checkSubscriptionAuthorize(data?.objectId),
    checkIfPatientEnrolledInWeightlossProgramin6Months(data?.objectId),
  ]);
  return {
    user: data,
    hasSubscription: subscription?.success,
    hasNotifications: notifications.count > 0 ? true : false,
    notifications: notifications?.results,
    isWeightlossDiscountEligible: ConsultationCount < 1 ? true : false,
  };
}
